import axios from 'axios';
import posthog from 'posthog-js';
import { store } from '../store';
import { logout, setCredentials } from '../store/authSlice';
import { PublicRoutePath } from '../config/routes';
import { appPath, isDemoMode } from '../demo/demoMode';
import { demoAdapter } from '../demo/adapter';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

if (isDemoMode) {
  api.defaults.adapter = demoAdapter;
  axios.defaults.adapter = demoAdapter;
}

// Cache public pages
const flattenRoutes = (routes: Record<string, any>): string[] => {
  return Object.values(routes).reduce((acc: string[], value) => {
    if (typeof value === 'string') {
      acc.push(value);
    } else if (typeof value === 'object' && value !== null) {
      acc.push(...flattenRoutes(value));
    }
    return acc;
  }, [] as string[]);
};

const publicPages = flattenRoutes(PublicRoutePath).filter(path => path !== '/').map(path => 
  new RegExp(path.replace(/:\w+/g, '.*'))
);

// Function to check if current page is public
const isPublicPage = (): boolean => {
  return publicPages.some(page => page.test(window.location.pathname));
};

// Helper function for redirecting to login if needed
const redirectToLoginIfNeeded = () => {
  // Clear any stored auth data
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Only redirect if we're not already on the login page or a public page
  if (!isPublicPage()) {
    window.location.href = appPath('/login');
  }
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Tie backend-captured PostHog exceptions to this frontend session/user
    if (import.meta.env.VITE_POSTHOG_KEY) {
      config.headers['x-posthog-distinct-id'] = posthog.get_distinct_id();
      config.headers['x-posthog-session-id'] = posthog.get_session_id();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing the token to prevent multiple refresh requests
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let pendingRequests: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

// Process all pending requests with the new token
const processPendingRequests = (token: string | null) => {
  pendingRequests.forEach(request => {
    if (token) {
      request.config.headers.Authorization = `Bearer ${token}`;
      request.resolve(api(request.config));
    } else {
      request.reject(new Error('Token refresh failed'));
    }
  });
  pendingRequests = [];
};

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Check if backend auto-refreshed the token
    const newAccessToken = response.headers['x-new-access-token'];
    
    if (newAccessToken) {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      let tokenSub: string | null = null;
      try {
        const payload = JSON.parse(atob(newAccessToken.split('.')[1]));
        tokenSub = payload.sub ?? null;
      } catch {
        console.warn('[API] Could not decode auto-refreshed token, discarding');
      }

      if (!tokenSub || !user || tokenSub !== user.id) {
        console.warn('[API] Discarding auto-refreshed token: sub mismatch or no session', tokenSub, '!=', user?.id);
      } else {
        console.log('[API] Token auto-refreshed by backend, updating localStorage and headers');

        localStorage.setItem('token', newAccessToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        store.dispatch(setCredentials({ token: newAccessToken, member: user }));
      }
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and not from auth endpoints and we haven't tried to refresh yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/logout') &&
      !originalRequest.url?.includes('/auth/refresh-token') &&
      !originalRequest.url?.includes('/register') &&
      !originalRequest.url?.includes('/register/member')
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;
        
        try {
          // Attempt to refresh the token without going through AuthService
          // to avoid circular dependency with BaseService.
          // Refresh token is sent automatically via httpOnly cookie
          const refreshResponse = await axios.post(
            `${api.defaults.baseURL}/auth/refresh-token`,
            {},
            { withCredentials: true }
          );
          const newToken: string = refreshResponse.data.token;
          
          // Update token in localStorage and Redux store
          localStorage.setItem('token', newToken);
          
          // Get current user from localStorage
          const userStr = localStorage.getItem('user');
          const user = userStr ? JSON.parse(userStr) : null;
          
          // Update Redux store with new token
          if (user) {
            store.dispatch(setCredentials({ token: newToken, member: user }));
          }
          
          // Update the failed request's Authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Process any pending requests with the new token
          processPendingRequests(newToken);
          
          // Retry the original request with new token
          return api(originalRequest);
        } catch (refreshError) {
          // Token refresh failed, logout user
          processPendingRequests(null);
          store.dispatch(logout());
          redirectToLoginIfNeeded();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // If another request is already refreshing, add this request to pending
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve,
            reject,
            config: originalRequest
          });
        });
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
